/**
 * Touch-safe horizontal slider.
 *
 * WHY THIS EXISTS (and isn't a native <input type="range">):
 * On mobile, a native range input commits a new value the instant you
 * touch it — at the touch-down x-position, *before* the gesture's
 * direction is known. So a vertical scroll that happens to start on the
 * slider jumps the value to wherever the finger landed (often max). CSS
 * `touch-action: pan-y` lets the page scroll, but it can't stop an
 * element that mutates on touch-down, so it doesn't fix the native input.
 *
 * The fix is the standard "axis-lock" pattern: don't change anything on
 * touch-down; on the first bit of movement, decide intent — if it's more
 * vertical than horizontal, treat it as a scroll and never touch the
 * value (the page scrolls because of `touch-action: pan-y`); only once a
 * gesture is clearly horizontal do we start adjusting. A plain tap (no
 * movement past the threshold) also leaves the value untouched.
 *
 * WHY NOT a library (e.g. @use-gesture/react): the axis-lock logic is a
 * few lines, and no mainstream slider *component* ships this behaviour as
 * a default anyway (Radix/MUI commit on track-press, with no opt-out), so
 * a dependency would only replace the small pointer block below while we
 * still hand-write the visuals, value mapping, keyboard, and ARIA. Not
 * worth a new dep for one control — see the repo's "ask before adding a
 * dependency" rule. Revisit if we grow more gesture-driven UI.
 */

import {
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { cn } from '@/lib/cn';

interface SliderProps {
  value: number;
  onChange: (next: number) => void;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
}

// How far a finger must travel before we lock the gesture to an axis.
// Small enough to feel responsive, large enough that a near-still tap
// never registers as a drag.
const AXIS_LOCK_THRESHOLD_PX = 6;
// Page Up/Down jump by this many steps for quick keyboard adjustment.
const PAGE_STEP_MULTIPLIER = 5;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

// Snap to the step grid and strip the floating-point noise that
// multiplying by a fractional step introduces (e.g. 0.7500000001).
function snapToStep(raw: number, min: number, max: number, step: number): number {
  const steps = Math.round((raw - min) / step);
  const decimals = (String(step).split('.')[1] ?? '').length;
  return clamp(Number((min + steps * step).toFixed(decimals)), min, max);
}

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  mode: 'pending' | 'sliding';
};

export function Slider({
  value,
  onChange,
  min,
  max,
  step,
  disabled = false,
  id,
  'aria-label': ariaLabel,
}: SliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  // Kept in a ref (not state) so a drag doesn't re-render every frame.
  const drag = useRef<DragState | null>(null);

  const fraction = max > min ? (clamp(value, min, max) - min) / (max - min) : 0;
  const percent = fraction * 100;

  function valueFromClientX(clientX: number): number {
    const el = trackRef.current;
    if (!el) return value;
    const rect = el.getBoundingClientRect();
    const ratio = rect.width > 0 ? clamp((clientX - rect.left) / rect.width, 0, 1) : 0;
    return snapToStep(min + ratio * (max - min), min, max, step);
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    // Ignore non-primary mouse buttons; touch/pen always start a gesture.
    if (disabled || (e.pointerType === 'mouse' && e.button !== 0)) return;
    // Record the start but DON'T change the value yet — that's the whole
    // point. Direction is decided on the first move (see onPointerMove).
    drag.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, mode: 'pending' };
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const state = drag.current;
    if (!state || state.pointerId !== e.pointerId) return;

    if (state.mode === 'pending') {
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_THRESHOLD_PX) return;
      if (Math.abs(dx) > Math.abs(dy)) {
        // Horizontal intent → take over and slide. Capturing the pointer
        // keeps move events coming even if the finger leaves the track.
        state.mode = 'sliding';
        trackRef.current?.setPointerCapture(e.pointerId);
      } else {
        // Vertical intent → abandon. `touch-action: pan-y` lets the
        // browser scroll the page; we never touch the value.
        drag.current = null;
        return;
      }
    }

    if (state.mode === 'sliding') {
      e.preventDefault();
      const next = valueFromClientX(e.clientX);
      if (next !== value) onChange(next);
    }
  }

  function endDrag(e: ReactPointerEvent<HTMLDivElement>) {
    const state = drag.current;
    if (state && state.pointerId === e.pointerId) {
      if (trackRef.current?.hasPointerCapture(e.pointerId)) {
        trackRef.current.releasePointerCapture(e.pointerId);
      }
      drag.current = null;
    }
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    let next: number;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = value + step;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = value - step;
        break;
      case 'PageUp':
        next = value + step * PAGE_STEP_MULTIPLIER;
        break;
      case 'PageDown':
        next = value - step * PAGE_STEP_MULTIPLIER;
        break;
      case 'Home':
        next = min;
        break;
      case 'End':
        next = max;
        break;
      default:
        return;
    }
    e.preventDefault();
    const snapped = snapToStep(next, min, max, step);
    if (snapped !== value) onChange(snapped);
  }

  return (
    <div
      ref={trackRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className={cn(
        // pan-y: vertical gestures scroll the page; horizontal gestures
        // are delivered to us and become slides (see the axis-lock above).
        'relative flex h-6 w-full touch-pan-y items-center',
        disabled ? 'opacity-50' : 'cursor-pointer',
      )}
    >
      <div className="h-2 w-full rounded-full bg-paper-border">
        <div className="h-2 rounded-full bg-accent-600" style={{ width: `${percent}%` }} />
      </div>
      <div
        role="slider"
        id={id}
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-disabled={disabled || undefined}
        aria-orientation="horizontal"
        onKeyDown={onKeyDown}
        className={cn(
          'absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 touch-pan-y rounded-full',
          'border-2 border-accent-600 bg-white shadow',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
          !disabled && 'active:cursor-grabbing',
        )}
        style={{ left: `${percent}%` }}
      />
    </div>
  );
}
